const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

/**
 * Client-side estimate of what the API will compute for an order (see
 * ServiceOrder.refresh_total_value). The server's total is authoritative.
 *
 * @param {{baseValue: number, quantity: number}[]} lines
 * @param {"VAL"|"PER"|""} discountType
 * @param {number} discountValue
 */
export const orderTotals = (lines, discountType, discountValue) => {
    const sum = lines.reduce((acc, {baseValue, quantity}) => acc + baseValue * quantity, 0);
    const subtotal = round2(Math.max(sum, 0));

    let discount = 0;
    if (discountValue > 0 && discountType === "PER") {
        discount = round2((subtotal * Math.min(discountValue, 100)) / 100);
    } else if (discountValue > 0 && discountType === "VAL") {
        discount = round2(discountValue);
    }

    return {subtotal, discount, total: round2(subtotal - discount)};
};

/**
 * What the order cost the provider: each line's cost (the one it was priced
 * with) times its quantity, services and products together. Null when no
 * line says what it cost (older answers), so nothing is shown as a false 0.
 *
 * @param {{cost?: string|number|null, quantity: number}[]} lines
 */
export const orderCost = (lines) => {
    const known = lines.filter((line) => line.cost !== undefined && line.cost !== null);
    if (known.length === 0) return null;
    return round2(known.reduce((acc, line) => acc + Number(line.cost) * line.quantity, 0));
};

/**
 * What the order earned the provider: the total the customer pays (after the
 * discount) minus what it cost, and that as a share of the total. The share
 * is null when the total is zero (nothing to take it of); the whole margin is
 * null when the cost is unknown.
 *
 * @param {number} total
 * @param {number|null} cost
 * @returns {{amount: number, percent: number|null}|null}
 */
export const orderMargin = (total, cost) => {
    if (cost === null || cost === undefined) return null;
    const amount = round2(total - cost);
    return {amount, percent: total > 0 ? Math.round(((amount / total) * 100 + Number.EPSILON) * 10) / 10 : null};
};
